'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Search, MoreHorizontal, Pause, Play, Trash2, Eye,
  Calendar, Repeat, Clock, CheckCircle, XCircle
} from 'lucide-react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { ChipProps } from '@mui/material'
import { toast } from '@/components/ui/use-toast'
import { PageHeader } from '@/components/ui/page-header'
import { PageLoader } from '@/components/ui/page-loader'
import { StatsCard } from "@crm-src/components/stats-card"
import { CreateRecurringScheduleModal } from '@crm-src/components/create-recurring-schedule-modal'
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import {
  invoiceSchedulesApi, scheduledInvoicesApi,
  formatFrequency
} from '@crm-src/lib/invoice-schedules-api'
import type { InvoiceSchedule, ScheduledInvoice } from '@crm-src/types/invoice-schedule'
import { InvoiceTabs } from '@crm-src/components/invoice-tabs'

// Maps a recurring-schedule status to a MUI Chip color (replaces the Tailwind
// class string from getScheduleStatusColor for the MUI migration).
function scheduleStatusChipColor(status: string): ChipProps['color'] {
  switch (status) {
    case 'active':
      return 'success'
    case 'paused':
      return 'warning'
    case 'completed':
      return 'primary'
    case 'cancelled':
    default:
      return 'default'
  }
}

export default function InvoiceSchedulesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const customerId = searchParams.get('customer_id')

  const [schedules, setSchedules] = useState<InvoiceSchedule[]>([])
  const [upcomingInvoices, setUpcomingInvoices] = useState<ScheduledInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [customerName, setCustomerName] = useState<string | null>(null)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [scheduleToCancel, setScheduleToCancel] = useState<InvoiceSchedule | null>(null)
  // Per-row actions menu anchor (keyed by schedule id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  // Statistics
  const [stats, setStats] = useState({
    active: 0,
    paused: 0,
    completed: 0,
    upcomingCount: 0
  })

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)

      // Fetch schedules with optional customer filter
      const schedulesResponse = await invoiceSchedulesApi.getAll({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined,
        customer_id: customerId || undefined
      })

      if (schedulesResponse.success && schedulesResponse.data) {
        setSchedules(schedulesResponse.data)

        // Calculate stats
        const activeCount = schedulesResponse.data.filter(s => s.status === 'active').length
        const pausedCount = schedulesResponse.data.filter(s => s.status === 'paused').length
        const completedCount = schedulesResponse.data.filter(s => s.status === 'completed').length

        setStats(prev => ({
          ...prev,
          active: activeCount,
          paused: pausedCount,
          completed: completedCount
        }))
      }

      // Fetch upcoming invoices with optional customer filter
      const upcomingResponse = await invoiceSchedulesApi.getUpcoming({
        limit: 5,
        days: 30,
        customer_id: customerId || undefined
      })
      if (upcomingResponse.success && upcomingResponse.data) {
        setUpcomingInvoices(upcomingResponse.data)
        setStats(prev => ({ ...prev, upcomingCount: upcomingResponse.data?.length || 0 }))

        // Get customer name from first schedule if filtering by customer
        if (customerId && schedulesResponse.data && schedulesResponse.data.length > 0) {
          const firstSchedule = schedulesResponse.data[0] as any
          if (firstSchedule.customer?.full_name || firstSchedule.customer?.company_name) {
            setCustomerName(firstSchedule.customer.full_name || firstSchedule.customer.company_name)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load invoice schedules',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, searchQuery, customerId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handlePause = async (id: string) => {
    try {
      const response = await invoiceSchedulesApi.pause(id)
      if (response.success) {
        toast({ title: 'Success', description: 'Schedule paused' })
        fetchData()
      } else {
        toast({
          title: 'Error',
          description: response.errors?.[0] || 'Failed to pause schedule',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to pause schedule', variant: 'destructive' })
    }
  }

  const handleResume = async (id: string) => {
    try {
      const response = await invoiceSchedulesApi.resume(id)
      if (response.success) {
        toast({ title: 'Success', description: 'Schedule resumed' })
        fetchData()
      } else {
        toast({
          title: 'Error',
          description: response.errors?.[0] || 'Failed to resume schedule',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to resume schedule', variant: 'destructive' })
    }
  }

  const handleCancelClick = (schedule: InvoiceSchedule) => {
    setScheduleToCancel(schedule)
    setShowCancelDialog(true)
  }

  const handleConfirmCancel = async () => {
    if (!scheduleToCancel) return

    try {
      const response = await invoiceSchedulesApi.delete(scheduleToCancel.id)
      if (response.success) {
        toast({ title: 'Success', description: 'Schedule cancelled' })
        fetchData()
      } else {
        toast({
          title: 'Error',
          description: response.errors?.[0] || 'Failed to cancel schedule',
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to cancel schedule', variant: 'destructive' })
    } finally {
      setShowCancelDialog(false)
      setScheduleToCancel(null)
    }
  }

  // Status icon for the Chip. Colors baked to the sRGB hex the old Tailwind
  // text-* utilities produced so the icon tint survives once Tailwind is gone.
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} color="#22c55e" />
      case 'paused':
        return <Pause size={16} color="#eab308" />
      case 'completed':
        return <CheckCircle size={16} color="#3b82f6" />
      case 'cancelled':
        return <XCircle size={16} color="#6b7280" />
      default:
        return null
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Tabs Navigation */}
      <InvoiceTabs />

      <PageHeader
        title={customerId ? `Recurring Schedules for ${customerName || 'Customer'}` : 'Recurring Schedules'}
        description={
          <>
            <Box component="span" sx={{ maxWidth: "48rem", display: "block" }}>
              {customerId
                ? 'Automatic invoice rules for this customer. Each schedule repeats on a set frequency.'
                : 'Automatic invoice rules. Set up a schedule once, and the system will automatically create and send invoices on a repeating basis (weekly, monthly, etc). Think of each schedule as a "rule" that keeps generating invoices for you.'}
            </Box>
            {customerId && (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate(`/crm/customers/${customerId}`)}
                sx={{ fontSize: 12, p: 0, color: "primary.main" }}
              >
                ← Back to customer
              </Link>
            )}
          </>
        }
        actions={
          <Button variant="contained" onClick={() => setIsCreateModalOpen(true)} startIcon={<Plus size={16} />}>
            New Schedule
          </Button>
        }
      />

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Active Schedules"
          value={stats.active}
          icon={Repeat}
          iconColor="text-green-500"
        />
        <StatsCard
          title="Paused"
          value={stats.paused}
          icon={Pause}
          iconColor="text-yellow-500"
        />
        <StatsCard
          title="Completed"
          value={stats.completed}
          icon={CheckCircle}
          iconColor="text-blue-500"
        />
        <StatsCard
          title="Sendouts (30 days)"
          value={stats.upcomingCount}
          icon={Calendar}
          iconColor="text-indigo-500"
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search schedules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1, maxWidth: 384 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={{ color: "text.disabled", display: "flex" }}><Search size={16} /></Box>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {/* Schedules Table */}
      <Card>
        <CardHeader
          title={<Typography component="span" sx={{ fontWeight: 600 }}>Recurring Schedules</Typography>}
          subheader="View and manage all invoice schedules"
        />
        <CardContent>
          {isLoading ? (
            <PageLoader />
          ) : schedules.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled" }}><Repeat size={48} /></Box>
              <Typography component="h3" sx={{ mt: 2, fontSize: "1.125rem", fontWeight: 500 }}>No schedules found</Typography>
              <Typography sx={{ mt: 1, color: "text.secondary" }}>Create a recurring schedule to automate invoice generation.</Typography>
              <Button variant="contained" onClick={() => setIsCreateModalOpen(true)} startIcon={<Plus size={16} />} sx={{ mt: 2 }}>
                Create Schedule
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Next Invoice</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ width: 70 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => {
                  const statusIcon = getStatusIcon(schedule.status)
                  return (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>{schedule.name}</Typography>
                        {schedule.description && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {schedule.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 14 }}>
                        {schedule.customer?.full_name || 'Unknown'}
                      </Typography>
                      {schedule.customer?.company_name && (
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          {schedule.customer.company_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: "text.disabled", display: "flex" }}><Repeat size={16} /></Box>
                        <Typography component="span">{formatFrequency(schedule.frequency, schedule.interval)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {schedule.next_scheduled_at ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ color: "text.disabled", display: "flex" }}><Clock size={16} /></Box>
                          <Typography component="span">
                            {new Date(schedule.next_scheduled_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography component="span" sx={{ color: "text.disabled" }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={scheduleStatusChipColor(schedule.status) ?? "default"}
                        {...(statusIcon ? { icon: statusIcon } : {})}
                        label={schedule.status}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" aria-label="Open menu" onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: schedule.id })}>
                        <MoreHorizontal size={16} />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor?.id === schedule.id ? menuAnchor.el : null}
                        open={menuAnchor?.id === schedule.id}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                        <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/crm/invoices/schedules/${schedule.id}`); }}>
                          <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                          View Details
                        </MenuItem>
                        <Divider />
                        {schedule.status === 'active' && (
                          <MenuItem onClick={() => { setMenuAnchor(null); handlePause(schedule.id); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Pause size={16} /></Box>
                            Pause
                          </MenuItem>
                        )}
                        {schedule.status === 'paused' && (
                          <MenuItem onClick={() => { setMenuAnchor(null); handleResume(schedule.id); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Play size={16} /></Box>
                            Resume
                          </MenuItem>
                        )}
                        <Divider />
                        <MenuItem onClick={() => { setMenuAnchor(null); handleCancelClick(schedule); }} sx={{ color: "error.main" }}>
                          <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                          Cancel Schedule
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Sendouts Preview */}
      {upcomingInvoices.length > 0 && (
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Calendar size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>Preview: Next 30 Days</Typography>
              </Box>
            }
            subheader="Quick preview of invoices coming up from these recurring schedules. Each one is generated on its date and a review copy is emailed to finance — nothing is sent to the customer automatically."
          />
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {upcomingInvoices.map((invoice) => (
                <Box
                  key={invoice.id}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 500 }}>
                      {invoice.invoice_schedule?.customer?.full_name || 'Unknown'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                      {invoice.invoice_schedule?.name} - ${Number(invoice.source_invoice?.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      {new Date(invoice.scheduled_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <CreateRecurringScheduleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={fetchData}
      />

      {/* Cancel Schedule Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title="Cancel Recurring Schedule?"
        confirmLabel="Yes, Cancel Schedule"
        cancelLabel="Keep Schedule"
        onConfirm={handleConfirmCancel}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography>
            Are you sure you want to cancel <strong>{scheduleToCancel?.name}</strong>?
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            This will permanently stop this recurring schedule. All pending invoices will be cancelled.
            Existing generated invoices will not be affected.
          </Typography>
        </Box>
      </ConfirmDialog>
    </Box>
  )
}
